import { CSSProperties } from '@stitches/react'
import { ComponentProps, FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '../../lib/stitches'
import { gray15, gray8 } from '../platform/color'
import { ChevronLeft, CloseIcon } from './icons'
import withTooltip from './withTooltip'

export const Image = styled('img', {
  variants: {
    square: {
      true: {
        '&:after': {
          content: '',
          display: 'block',
          paddingBottom: '100%'
        }
      }
    }
  }
})

/**
 *  The most abstract component in this file
 *  Use it for building quick layouts that require styling
 */
export const Box = styled('div', {
  variants: {
    unselectable: {
      true: {
        WebkitUserSelect: 'none', // Safari
        MozUserSelect: 'none', // Firefox
        MsUserSelect: 'none', // IE10+/Edge
        userSelect: 'none'
      }
    },
    animateAppear: {
      true: {
        animation: 'appear 200ms ease-out'
      }
    }
  }
})

export const Row = styled(Box, {
  display: 'flex',
  flexDirection: 'row',
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse'
      }
    },
    verticalCenter: {
      true: {
        alignItems: 'center'
      }
    },
    horizontalCenter: {
      true: {
        justifyContent: 'center'
      }
    },
    justifySpaceBetween: {
      true: {
        justifyContent: 'space-between'
      }
    },
    justifyContentSpaceAround: {
      true: {
        justifyContent: 'space-around'
      }
    },
    justifyContentSpaceEvenly: {
      true: {
        justifyContent: 'space-evenly'
      }
    },
    justifyContentEnd: {
      true: {
        justifyContent: 'end'
      }
    },
    alignItemsStretch: {
      true: {
        alignItems: 'stretch'
      }
    }
  }
})

export const Column = styled(Box, {
  display: 'flex',
  flexDirection: 'column',
  variants: {
    horizontalCenter: {
      true: {
        alignItems: 'center'
      }
    },
    verticalCenter: {
      true: {
        justifyContent: 'center'
      }
    },
    alignItemsStretch: {
      true: {
        alignItems: 'stretch'
      }
    },
    alignItemsSpaceBetween: {
      true: {
        alignItems: 'space-between'
      }
    },
    justifySpaceBetween: {
      true: {
        justifyContent: 'space-between'
      }
    }
  }
})

export const VerticalCenteredRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
})

export const CenteredRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center'
})

export const CenteredColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center'
})

interface CircleButtonProps {
  css?: {}
  onClick?: (e: any) => void
  children: any
  size: string | number
  fill: string
}
export const CircleButton: FC<CircleButtonProps> = (props) => (
  <Row
    css={{
      width: props.size,
      height: props.size,
      backgroundColor: props.fill,
      borderRadius: '50%',
      cursor: 'pointer',
      '&:hover': {
        filter: 'box-shadow: 0px 2.97006px 20px rgba(209, 217, 230, 0.6)'
      },
      ...props.css
    }}
    verticalCenter
    horizontalCenter
    {...props}
  >
    {props.children}
  </Row>
)

export const BackButton: FC<any> = (props) => {
  const navigate = useNavigate()
  return (
    <CircleButton fill="#FFFFFF" size="40px" onClick={() => navigate(-1)} {...props}>
      <ChevronLeft />
    </CircleButton>
  )
}

interface SquareIframeProps {
  src: string
  style?: CSSProperties
  isPlayer?: boolean
}
export const SquareIframe: FC<SquareIframeProps> = ({ src, style, isPlayer }) => (
  <div
    style={{
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      paddingTop: '100%',
      boxShadow: isPlayer && '0px 4px 16px 0px #00000026',
      borderRadius: isPlayer && '10px',
      ...style
    }}
  >
    <iframe
      style={{
        position: 'absolute',
        top: '0',
        left: '0',
        bottom: '0',
        right: '0',
        width: '100%',
        height: '100%'
      }}
      src={src}
    />
  </div>
)

export const TagButton: FC<
  { backgroundColor: string; color: string } & ComponentProps<typeof VerticalCenteredRow>
> = ({ backgroundColor, color, children, style = {}, className = '' }) => {
  return (
    <VerticalCenteredRow
      css={{
        backgroundColor: backgroundColor,
        color: color,
        padding: '3px 10px',
        borderRadius: '20px',
        ...style
      }}
      className={className}
    >
      {children}
    </VerticalCenteredRow>
  )
}

/**
 * A flexible flex spacer that expands along the major axis of its containing flex layout.
 * It renders a `div` by default, and takes up any available space.
 */
export const Spacer = styled('div', {
  flex: 1,
  justifySelf: 'stretch',
  alignSelf: 'stretch'
})

export const Text = styled('p')

export const Divider = styled(Box, {
  width: '100%',
  borderTopWidth: '1px',
  borderTopColor: '$gray10',
  borderTopStyle: 'solid',
  marginTop: '$16',
  marginBottom: '$16'
})

export const IconButton = styled('button', {
  all: 'unset',
  width: '25px',
  height: '25px',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'transparent',
  borderRadius: '6px',
  transition: 'all 100ms ease-out',
  '&:hover': { background: 'rgba(0,0,0,.05)' },
  '&:active': { background: 'rgba(0,0,0,.1)' },
  '&:focus': { boxShadow: '0px 0px 0px 2px inset rgba(0,0,0,0.1)' },
  '&:disabled': { background: 'transparent', cursor: 'not-allowed', color: 'rgba(0,0,0,.3)' },
  variants: {
    active: {
      true: {
        color: 'rgba(71, 145, 255, 1)',
        background: 'rgba(71, 145, 255, 0.1)',
        '&:hover': { background: 'rgba(71, 145, 255, 0.15)' },
        '&:active': { background: 'rgba(71, 145, 255, 0.25)' }
      }
    },
    danger: {
      true: {
        color: 'rgba(244, 62, 114, 1)',
        background: 'rgba(244, 62, 114, 0.1)',
        '&:hover': { background: 'rgba(244, 62, 114, 0.15)' },
        '&:active': { background: 'rgba(244, 62, 114, 0.25)' }
      }
    },
    secondary: {
      true: {
        color: 'rgba(0,0,0,.5)'
      }
    },
    round: {
      true: {
        borderRadius: '50%'
      }
    }
  }
})

export const StyledScrollable = styled(Box, {
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '4px',
    height: '6px'
  },

  /* Track */
  '&::-webkit-scrollbar-track': {
    borderRadius: '10px'
  },

  /* Handle */
  '&::-webkit-scrollbar-thumb': {
    background: gray8,
    borderRadius: '10px'
  }
})

export const HorizontalStyledScrollable = styled(Box, {
  overflowX: 'auto',

  '&::-webkit-scrollbar': {
    height: '4px'
  },

  /* Track */
  '&::-webkit-scrollbar-track': {
    borderRadius: '10px'
  },

  /* Handle */
  '&::-webkit-scrollbar-thumb': {
    background: gray8,
    borderRadius: '10px'
  }
})

export const CloseButton: FC<{ close: () => void }> = ({ close }) => (
  <Row
    onClick={close}
    css={{
      width: '30px',
      height: '30px',
      cursor: 'pointer',
      padding: '5px 5px 5px 5px',
      borderRadius: '50%',
      transition: '150ms',
      background: 'none',
      '&:hover': {
        backgroundColor: 'var(--studio-very-light)'
      }
    }}
    horizontalCenter
    verticalCenter
  >
    <CloseIcon fill={gray15} style={{ width: '20px', height: '20px' }} />
  </Row>
)

// export const ResponsiveContainer = styled('div', {
//   paddingLeft: '16px',
//   paddingRight: '16px',
//   maxWidth: '1080px',
//   margin: 'auto',
//   '@tablet': {
//     paddingLeft: '24px',
//     paddingRight: '24px'
//   }
// })

export const TooltipIconButton = withTooltip(IconButton)
